import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import SaveIcon from "@mui/icons-material/Save";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import classNames from "classnames";
import { useMemo, useState } from "react";

import { RobotoTooltip } from "@/shared/components";
import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  type DashboardAccessibility,
  DashboardRecord,
  isDashboardRecord,
} from "@/shared/domain/dashboards";
import {
  useCreateDashboard,
  useDashboards,
  useDeleteDashboard,
  useUpdateDashboard,
} from "@/shared/domain/dashboards/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  RobotoDashboardRevisionConflictException,
  RobotoDomainException,
  userFacingErrorMessage,
} from "@/shared/http";

import styles from "./DashboardMenu.module.css";
import { DashboardDefinition } from "./DashboardParams";

/** Why the menu is switching dashboards: a row pick / Save-as landing
 *  (`select` — the owner records it as the remembered choice), the "New
 *  dashboard" action (`new`), or a completed delete (`deleted`). The owner
 *  branches on this so intent is never inferred from the id alone. */
export type DashboardSelectReason = "select" | "new" | "deleted";

export interface DashboardMenuProps {
  /** The currently loaded dashboard id, or null for an unsaved view. */
  activeDashboardId: string | null;
  /** True when the loaded dashboard's definition differs from the live view state. */
  isDirty: boolean;
  /** Snapshot of current view state to persist on Save / Save-as. */
  currentDefinition: DashboardDefinition;
  /** The revision the canvas was seeded from, sent with a definition save so the
   *  server can reject it if someone else has since replaced the definition.
   *  Null when there is nothing to save into (an unsaved view), in which case
   *  Save falls back to Save-as. Supplied by the owner from its seed-time
   *  capture — never read from a query cache, which can drift to a concurrent
   *  writer's revision and defeat the check. */
  baseRevision: number | null;
  /** Switch to a saved dashboard (or null for an unsaved view). */
  onSelect: (
    dashboardId: string | null,
    meta: { reason: DashboardSelectReason },
  ) => void;
  /** A Save or Save-as succeeded: the backend record (whose id is passed —
   *  the NEW record's for Save-as) now matches the live view. The owner uses
   *  this to drop its unsaved-edits draft and to record the saved dashboard
   *  as the remembered choice. Fires before `onSelect` on Save-as. */
  onSaved?: (saved: DashboardRecord) => void;
  /** A definition save was rejected because the dashboard changed first. The
   *  owner resolves it (the conflict dialog acts on state it holds). */
  onRevisionConflict?: (serverRecord: DashboardRecord) => void;
  /** Closed-field label for the unsaved view (no dashboard selected).
   *  Defaults to "Untitled dashboard"; the owner passes "Dashboard Showcase"
   *  when the unsaved view is the priority-3 showcase landing. */
  unsavedLabel?: string;
  /** The header's Restore button was pressed: discard the live edits (and
   *  any stored draft) and re-apply the loaded record's saved definition.
   *  Only reachable while a dirty saved dashboard is loaded — the button
   *  shares the Save affordance's visibility gate. */
  onRestore?: () => void;
}

type SaveAsForm = {
  name: string;
  accessibility: DashboardAccessibility;
};

/** Matches the app's error-toast duration for mutation failures
 * (FileBrowserDndContext). */
const ERROR_NOTIFICATION_DURATION_MS = 6000;

/**
 * String twin of `userFacingErrorMessage` for `showNotification`, which only
 * accepts strings (the shared helper returns a ReactNode fallback). Same
 * contract: a backend 4XX message is server-authored and safe to show
 * verbatim; anything else gets the operation-specific fallback.
 */
function toastErrorMessage(error: unknown, fallback: string): string {
  return error instanceof RobotoDomainException && error.isClientError()
    ? error.message
    : fallback;
}

/**
 * Saved-dashboard picker + save/rename/delete controls for the metrics
 * dashboard. The owner (`MetricsDashboard`) holds the live view state and
 * hands in a snapshot (`currentDefinition`); saving persists that snapshot
 * into the backend record, and loading seeds the owner's state back from the
 * record.
 */
export function DashboardMenu({
  activeDashboardId,
  isDirty,
  currentDefinition,
  baseRevision,
  onSelect,
  onSaved,
  onRevisionConflict,
  onRestore,
  unsavedLabel,
}: DashboardMenuProps) {
  const dashboardsQuery = useDashboards();
  const dashboards = useMemo(
    () => dashboardsQuery.data ?? [],
    [dashboardsQuery.data],
  );

  const showNotification = useNotification();

  // Header Save's own mutation instance. The dialogs each own theirs (created
  // inside the conditionally-mounted dialog components), so a header-save
  // failure can never bleed into the Rename dialog and vice versa.
  const saveMutation = useUpdateDashboard();

  const active = useMemo(
    () => dashboards.find((d) => d.dashboard_id === activeDashboardId) ?? null,
    [dashboards, activeDashboardId],
  );

  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // The picker is a real MUI `<Select>` (same outlined-field base as the
  // time-range picker), so its rows are `<option>`s addressed by `value`. The
  // action rows carry sentinel values that this one `onChange` routes to the
  // matching effect instead of a dashboard selection; a dashboard row's value
  // is its id. (Extends the file's original `__new__` sentinel pattern.)
  const handleChange = (event: SelectChangeEvent) => {
    switch (event.target.value) {
      case "__new__":
        onSelect(null, { reason: "new" });
        return;
      case "__save__":
        handleSave();
        return;
      case "__saveas__":
        setSaveAsOpen(true);
        return;
      case "__rename__":
        setRenameOpen(true);
        return;
      case "__delete__":
        setConfirmDeleteOpen(true);
        return;
      default: {
        // A dashboard row: its value is the id. (The empty-value fallback is
        // belt-and-braces — no selectable row carries "".)
        const dashboardId = event.target.value || null;
        onSelect(dashboardId, {
          reason: dashboardId === null ? "new" : "select",
        });
      }
    }
  };

  const handleSave = () => {
    if (active === null || baseRevision === null) {
      // Falls back to Save-as when there's nothing to save into. A missing base
      // revision means the canvas was never seeded from a record, so there is
      // no revision to write against — same outcome, and a definition write
      // must never go out unguarded.
      setSaveAsOpen(true);
      return;
    }
    saveMutation.mutate(
      {
        dashboardId: active.dashboard_id,
        updates: {
          dashboard_definition: currentDefinition,
          base_revision: baseRevision,
        },
      },
      {
        onSuccess: (record) => onSaved?.(record),
        onError: (error) => {
          // A conflict is resolvable, so it earns a dialog rather than a
          // 6-second toast that offers the user nothing to act on.
          if (error instanceof RobotoDashboardRevisionConflictException) {
            const serverRecord = error.currentDashboard;
            if (isDashboardRecord(serverRecord)) {
              onRevisionConflict?.(serverRecord);
              return;
            }
            // A malformed payload can't drive the dialog; fall through to the
            // ordinary failure surface rather than rendering an empty one.
          }
          // No dialog is on screen, so the failure surfaces as an error
          // toast (the app's pattern for dialog-less mutation failures —
          // see FileBrowserDndContext, CollectionsView).
          showNotification(
            toastErrorMessage(
              error,
              "Failed to save dashboard. Please try again.",
            ),
            "error",
            ERROR_NOTIFICATION_DURATION_MS,
          );
        },
      },
    );
  };

  // Ownership is the sole org-wide/personal signal: a dashboard is org-wide
  // exactly when its owner is the org principal. The non-org remainder is safe
  // to treat as "yours" because the list only ever contains org-wide
  // dashboards plus the caller's own personal ones.
  const orgDashboards = dashboards.filter(
    (d) => d.owner_principal_id === `org:${d.org_id}`,
  );
  const userDashboards = dashboards.filter(
    (d) => d.owner_principal_id !== `org:${d.org_id}`,
  );

  // Only a saved, loaded dashboard can be dirty. The single visibility
  // predicate for every "there are unsaved edits to act on" affordance: the
  // closed field's dirty marker, the dropdown's Save row, and the header's
  // Save + Restore icon buttons. (Save-as's menu row is always offered; its
  // header button shows only when Save/Restore don't — see below.)
  const activeIsDirty = active !== null && isDirty;

  const unsavedChangesText = (
    <span className={styles.dashboardEditedText}> (unsaved changes)</span>
  );
  /**
   * One saved-dashboard row, shared by both accessibility groups. As a
   * `<Select>` child it's an option addressed by `value` (its id); the Select
   * routes the click to `handleChange` and marks the matching option
   * selected, so the row carries no `onClick`.
   */
  const renderDashboardRow = (record: DashboardRecord) => {
    const isActive = record.dashboard_id === activeDashboardId;
    const rowIsDirty = isActive && isDirty;
    return (
      <MenuItem
        key={record.dashboard_id}
        value={record.dashboard_id}
        className={classNames(styles.dashboardRow, {
          [styles.dashboardRowActive]: isActive,
        })}
      >
        <span className={styles.dashboardRowName}>
          {record.name}
          {rowIsDirty && unsavedChangesText}
        </span>
      </MenuItem>
    );
  };

  return (
    <div className={styles.root}>
      {/* One-click icon twins of the dropdown's Save / Save as rows, plus
       * Restore (which has no row: it reverts to the record the other two
       * write). The cluster shows exactly one save affordance: Save +
       * Restore behind `activeIsDirty`, Save-as only when they're hidden
       * (its dropdown row stays always available). Tooltips follow the
       * feature's RobotoTooltip-around-IconButton pattern
       * (MetricDefinitionsTable); the aria-labels are distinct from the
       * dialogs' "Save" buttons because test/getByRole name matching is
       * substring-based. */}
      <div className={styles.headerActions}>
        {activeIsDirty && (
          <RobotoTooltip title="Save dashboard">
            <span>
              <IconButton
                size="small"
                aria-label="Save dashboard"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </RobotoTooltip>
        )}
        {!activeIsDirty && (
          <RobotoTooltip title="Save dashboard as">
            <span>
              <IconButton
                size="small"
                aria-label="Save dashboard as"
                onClick={() => setSaveAsOpen(true)}
              >
                <SaveAsIcon fontSize="small" />
              </IconButton>
            </span>
          </RobotoTooltip>
        )}
        {activeIsDirty && (
          <RobotoTooltip title="restore last saved version">
            <span>
              <IconButton
                size="small"
                aria-label="restore last saved version"
                onClick={() => onRestore?.()}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </RobotoTooltip>
        )}
      </div>
      {/* Same outlined-field base as the time-range picker
       * (DashboardParams): FormControl + notched, floating "Dashboard" label
       * + Select chrome, so the two header fields read as one family. The
       * empty value ("" = unsaved view) renders a real placeholder via
       * `displayEmpty`, so the label must stay floated (`shrink` + `notched`)
       * or MUI would draw the resting label over "Unsaved view". */}
      <FormControl size="small" className={styles.select}>
        <InputLabel id="dashboard-label" shrink>
          Dashboard
        </InputLabel>
        <Select
          labelId="dashboard-label"
          label="Dashboard"
          notched
          displayEmpty
          value={activeDashboardId ?? ""}
          onChange={handleChange}
          renderValue={(value) => {
            if (!value) {
              return (
                <span className={styles.dashboardRowName}>
                  {unsavedLabel ?? "Untitled dashboard"}
                </span>
              );
            }
            const record = dashboards.find((d) => d.dashboard_id === value);
            return (
              <>
                <span className={styles.dashboardRowName}>
                  {record?.name ?? "Untitled dashboard"}
                  {activeIsDirty && unsavedChangesText}
                </span>
              </>
            );
          }}
        >
          {/* Context-sensitive action rows (New / Save / Save as / Rename /
           * Delete), then the accessibility-grouped list. Each action row is
           * an option with a sentinel value routed by `handleChange`; icons
           * are decorative (aria-hidden), the text label carries meaning. */}
          <MenuItem value="__new__" className={styles.actionRow}>
            <NoteAddOutlinedIcon
              aria-hidden="true"
              fontSize="small"
              className={styles.actionIcon}
            />
            New dashboard
          </MenuItem>
          {activeIsDirty && (
            <MenuItem
              value="__save__"
              className={styles.actionRow}
              disabled={saveMutation.isPending}
            >
              <SaveIcon
                aria-hidden="true"
                fontSize="small"
                className={styles.actionIcon}
              />
              Save
            </MenuItem>
          )}
          <MenuItem value="__saveas__" className={styles.actionRow}>
            <SaveAsIcon
              aria-hidden="true"
              fontSize="small"
              className={styles.actionIcon}
            />
            Save as…
          </MenuItem>
          {active !== null && (
            <MenuItem value="__rename__" className={styles.actionRow}>
              <EditOutlinedIcon
                aria-hidden="true"
                fontSize="small"
                className={styles.actionIcon}
              />
              Rename
            </MenuItem>
          )}
          {active !== null && (
            <MenuItem
              value="__delete__"
              className={classNames(styles.actionRow, styles.deleteRow)}
            >
              <DeleteOutlineIcon
                aria-hidden="true"
                fontSize="small"
                className={styles.actionIcon}
              />
              Delete
            </MenuItem>
          )}

          {/* The `.list-start` top border replaces a `<Divider />`: a Select
           * clones every child as an <option>, so a real Divider would render
           * as a stray, empty option. */}
          <ListSubheader
            disableSticky
            className={classNames(styles.groupHeader, styles.listStart)}
          >
            Shared
          </ListSubheader>
          {orgDashboards.length === 0 ? (
            <MenuItem disabled className={styles.emptyRow}>
              No shared dashboards yet
            </MenuItem>
          ) : (
            orgDashboards.map(renderDashboardRow)
          )}

          <ListSubheader
            disableSticky
            className={classNames(styles.groupHeader, styles.groupHeaderSpaced)}
          >
            Personal
          </ListSubheader>
          {userDashboards.length === 0 ? (
            <MenuItem disabled className={styles.emptyRow}>
              No personal dashboards yet
            </MenuItem>
          ) : (
            userDashboards.map(renderDashboardRow)
          )}
        </Select>
      </FormControl>

      {saveAsOpen && (
        <SaveAsDialog
          defaultName={active?.name ?? ""}
          currentDefinition={currentDefinition}
          onClose={() => setSaveAsOpen(false)}
          onCreated={(record) => {
            // Clear the owner's draft for the CURRENT (pre-switch) context —
            // and record the NEW record as the remembered choice — before
            // selecting it.
            onSaved?.(record);
            onSelect(record.dashboard_id, { reason: "select" });
            setSaveAsOpen(false);
          }}
        />
      )}

      {renameOpen && active !== null && (
        <RenameDialog
          dashboardId={active.dashboard_id}
          currentName={active.name}
          onClose={() => setRenameOpen(false)}
          onRenamed={() => setRenameOpen(false)}
        />
      )}

      {confirmDeleteOpen && active !== null && (
        <ConfirmDeleteDialog
          dashboardId={active.dashboard_id}
          name={active.name}
          onClose={() => setConfirmDeleteOpen(false)}
          onDeleted={() => {
            setConfirmDeleteOpen(false);
            onSelect(null, { reason: "deleted" });
          }}
        />
      )}
    </div>
  );
}

function SaveAsDialog({
  defaultName,
  currentDefinition,
  onClose,
  onCreated,
}: {
  defaultName: string;
  currentDefinition: DashboardDefinition;
  onClose: () => void;
  onCreated: (record: DashboardRecord) => void;
}) {
  const [form, setForm] = useState<SaveAsForm>({
    name: defaultName,
    accessibility: "user",
  });
  // The mutation lives inside the dialog, which is conditionally mounted:
  // closing it discards pending/error state, so every open starts fresh
  // (the visualizer's SaveLayoutConfirmation / DeleteLayoutConfirmation
  // pattern).
  const createMutation = useCreateDashboard();

  const handleSubmit = () => {
    createMutation.mutate(
      {
        name: form.name,
        accessibility: form.accessibility,
        dashboard_definition: currentDefinition,
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Save dashboard</DialogTitle>
      <DialogContent>
        <div className={styles.dialogBody}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
            size="small"
            disabled={createMutation.isPending}
          />
          <RadioGroup
            value={form.accessibility}
            onChange={(_e, value) =>
              setForm({
                ...form,
                accessibility: value as DashboardAccessibility,
              })
            }
          >
            <FormControlLabel
              value="user"
              control={<Radio size="small" />}
              label="Personal (only you)"
            />
            <FormControlLabel
              value="organization"
              control={<Radio size="small" />}
              label="Shared with organization"
            />
          </RadioGroup>
          {createMutation.isError && (
            <Alert severity="error">
              {userFacingErrorMessage(
                createMutation.error,
                "Failed to save dashboard. Please try again.",
              )}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={createMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMutation.isPending || form.name.trim().length === 0}
          loading={createMutation.isPending}
        >
          {createMutation.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RenameDialog({
  dashboardId,
  currentName,
  onClose,
  onRenamed,
}: {
  dashboardId: string;
  currentName: string;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const [name, setName] = useState(currentName);
  // Dialog-owned mutation: unmount-on-close guarantees a fresh error/pending
  // state on every open (see SaveAsDialog).
  const renameMutation = useUpdateDashboard();

  const handleSubmit = () => {
    renameMutation.mutate(
      { dashboardId, updates: { name } },
      { onSuccess: () => onRenamed() },
    );
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Rename dashboard</DialogTitle>
      <DialogContent>
        <div className={styles.dialogBody}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            size="small"
            disabled={renameMutation.isPending}
          />
          {renameMutation.isError && (
            <Alert severity="error">
              {userFacingErrorMessage(
                renameMutation.error,
                "Failed to rename dashboard. Please try again.",
              )}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={renameMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            renameMutation.isPending ||
            name.trim().length === 0 ||
            name === currentName
          }
          loading={renameMutation.isPending}
        >
          {renameMutation.isPending ? "Renaming…" : "Rename"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDeleteDialog({
  dashboardId,
  name,
  onClose,
  onDeleted,
}: {
  dashboardId: string;
  name: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  // Dialog-owned mutation: unmount-on-close guarantees a fresh error/pending
  // state on every open (see SaveAsDialog).
  const deleteMutation = useDeleteDashboard();

  const handleConfirm = () => {
    deleteMutation.mutate(dashboardId, { onSuccess: () => onDeleted() });
  };

  // Confirm-button label doubles as the state cue: pending shows progress,
  // a failure invites another attempt (SaveLayoutConfirmation's "Retry").
  let confirmLabel = "Delete";
  if (deleteMutation.isPending) {
    confirmLabel = "Deleting…";
  } else if (deleteMutation.isError) {
    confirmLabel = "Retry";
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Delete dashboard</DialogTitle>
      <DialogContent>
        <div className={styles.dialogBody}>
          <div>
            Delete <strong>{name}</strong>? This can&apos;t be undone.
          </div>
          {deleteMutation.isError && (
            <Alert severity="error">
              {userFacingErrorMessage(
                deleteMutation.error,
                "Failed to delete dashboard. Please try again.",
              )}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={deleteMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={deleteMutation.isPending}
          loading={deleteMutation.isPending}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Re-export for tests / consumers that want to type the active record. */
export type { DashboardRecord };
